import { IconButton, Menu, toast } from '@neo4j-ndl/react';
import { EllipsisHorizontalIconOutline } from '@neo4j-ndl/react/icons';
import { type FleetManagementTypes, fleetManagementApi, useActiveProjectSummary } from '@nx/state';
import { DangerConfirmModal } from '@nx/ui';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { RenameDbmsModal } from '../modals/rename-dbms-modal';

type DeploymentActionModalsProps = {
  dbms: FleetManagementTypes.DbmsDetails;
  isRenameModalOpen: boolean;
  setIsRenameModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isUnregisterDbmsModalOpen: boolean;
  setIsUnregisterDbmsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const DeploymentActionModals = ({
  dbms,
  isRenameModalOpen,
  setIsRenameModalOpen,
  isUnregisterDbmsModalOpen,
  setIsUnregisterDbmsModalOpen,
}: DeploymentActionModalsProps) => {
  const activeProjectSummary = useActiveProjectSummary();

  const [unregisterDbms] = fleetManagementApi.useUnregisterDbmsMutation();
  const [unregisterDbmsLoading, setUnregisterDbmsLoading] = useState(false);

  return (
    <>
      <DangerConfirmModal
        open={isUnregisterDbmsModalOpen}
        onClose={() => setIsUnregisterDbmsModalOpen(false)}
        title="Unregister deployment"
        content="Are you sure you want to unregister this deployment?"
        confirmButtonText="Unregister"
        onConfirm={() => {
          setUnregisterDbmsLoading(true);
          unregisterDbms({ projectId: activeProjectSummary?.id ?? '', dbmsId: dbms.dbmsId })
            .then(({ error }) => {
              if (error) {
                toast.danger('Failed to unregister deployment', { shouldAutoClose: true });
              } else {
                toast.success(`Deployment unregistered`, { shouldAutoClose: true });
              }
            })
            .catch((err: Error) => {
              toast.danger(err.message, { shouldAutoClose: true });
            })
            .finally(() => {
              setIsUnregisterDbmsModalOpen(false);
              setUnregisterDbmsLoading(false);
            });
        }}
        loading={unregisterDbmsLoading}
        error={null}
      />
      <RenameDbmsModal
        isOpen={isRenameModalOpen}
        closeModal={() => setIsRenameModalOpen(false)}
        dbmsId={dbms.dbmsId}
        currentName={dbms.name}
      />
    </>
  );
};

type DeploymentTableRowActionProps = {
  dbms: FleetManagementTypes.DbmsDetails;
  handleDisplayDbmsDetails: (dbms: FleetManagementTypes.DbmsDetails) => void;
};

export const DeploymentActionMenu = ({ dbms, handleDisplayDbmsDetails }: DeploymentTableRowActionProps) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isUnregisterDbmsModalOpen, setIsUnregisterDbmsModalOpen] = useState(false);

  const menuRef = useRef<HTMLButtonElement>(null);

  const onDetails = () => {
    void navigate(`${dbms.dbmsId}/details/`);
  };

  return (
    <>
      <IconButton ariaLabel="Deployment actions" onClick={() => setIsOpen(!isOpen)} ref={menuRef} isClean>
        <EllipsisHorizontalIconOutline className="h-full w-full" />
      </IconButton>
      <Menu isOpen={isOpen} anchorRef={menuRef} onClose={() => setIsOpen(false)}>
        <Menu.Items>
          <Menu.Item
            title="Inspect"
            onClick={() => {
              handleDisplayDbmsDetails(dbms);
              setIsOpen(false);
            }}
          />
          <Menu.Item
            title="Show details"
            onClick={() => {
              onDetails();
              setIsOpen(false);
            }}
          />
          <Menu.Item
            title="Rename"
            onClick={() => {
              setIsRenameModalOpen(true);
              setIsOpen(false);
            }}
          />
          <Menu.Divider />
          <Menu.Item
            title="Unregister deployment"
            className="text-danger-text"
            onClick={() => setIsUnregisterDbmsModalOpen(true)}
          />
        </Menu.Items>
      </Menu>
      <DeploymentActionModals
        dbms={dbms}
        isRenameModalOpen={isRenameModalOpen}
        setIsRenameModalOpen={setIsRenameModalOpen}
        isUnregisterDbmsModalOpen={isUnregisterDbmsModalOpen}
        setIsUnregisterDbmsModalOpen={setIsUnregisterDbmsModalOpen}
      />
    </>
  );
};
